import { useEffect, useState } from "react";
import {
  Layout,
  Button,
  Card,
  Spin,
  Flex,
  Descriptions,
  Tag,
  Divider,
  Row,
  Col,
  Image,
  Table,
  Badge,
} from "antd";
import { ReloadOutlined, RollbackOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import VendorDetailCard from "./blocs/VendorDetailCard";

import { makeFileURL, postAPI } from "../../utils/common";
import { VENDOR_BY_ID } from "../../constants/api";
import {
  BILLING_CYCLE,
  CURRENCY,
  getHotelType,
  HOTEL_ID_SERIES,
} from "../../constants/constent";

const { Content } = Layout;

/* ================= COMPONENT ================= */

function ViewVendor() {
  const navigate = useNavigate();
  const { edit_id } = useParams();

  const [loading, setLoading] = useState(false);
  const [vendor, setVendorDetail] = useState(null);

  /* ================= INIT ================= */

  useEffect(() => {
    fetchVendorDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= API CALL ================= */

  const fetchVendorDetail = () => {
    if (!edit_id) return;

    setLoading(true);
    postAPI(
      VENDOR_BY_ID,
      { id: edit_id },
      ({ status, data }) => {
        if (status && data) {
          setVendorDetail(data);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  /* ================= HELPERS ================= */

  const renderStatusText = (value) => {
    if (value === true || value === "approved")
      return { color: "green", text: "Approved" };
    if (value === false || value === "pending")
      return { color: "orange", text: "Pending" };
    if (value === false || value === "reject")
      return { color: "red", text: "Rejected" };
    return { text: "NA" };
  };

  const renderBillStatus = (value) => {
    if (value === true || value === 1) return <Tag color="green">Paid</Tag>;
    if (value === false || value === 0)
      return <Tag color="orange">Pending</Tag>;
    return <Tag>NA</Tag>;
  };
  const renderVendorStatus = (value) => {
    if (value === "pending") return <Tag color="green">Pending</Tag>;
    if (value === "active") return <Tag color="orange">Active</Tag>;
    if (value === "inactive") return <Tag color="orange">Inactive</Tag>;
    return <Tag>NA</Tag>;
  };

  const getBillingCycleLabel = (value) => {
    const match = BILLING_CYCLE.find((c) => c.value === Number(value));
    return match ? match.label : "-";
  };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD-MM-YYYY") : "-";

  /* ================= JSX ================= */

  return (
    <Layout>
      <LayoutHeader />

      <Content style={{ padding: 16 }}>
        <Card
          title="Vendor Detail"
          extra={
            <Flex wrap gap="small" justify="flex-end">
              <Button
                ghost
                type="primary"
                loading={loading}
                onClick={fetchVendorDetail}
              >
                <ReloadOutlined />
              </Button>
              <Button ghost type="primary" onClick={() => navigate(-1)}>
                <RollbackOutlined />
              </Button>
            </Flex>
          }
        >
          <Spin spinning={loading}>
            {vendor && (
              <>
                {/* ================= SUMMARY CARD ================= */}
                <div style={{ marginBottom: 20 }}>
                  <VendorDetailCard vendor={vendor} />
                </div>

                {/* ================= VENDOR INFO ================= */}
                <Descriptions
                  title="Vendor Information"
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 1, md: 3, lg: 4 }}
                >
                  <Descriptions.Item label="Hotel ID">
                    {HOTEL_ID_SERIES + vendor.hotel_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Vendor Name">
                    {vendor.vendor_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Hotel Name">
                    {vendor.hotel_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Hotel Type">
                    {getHotelType(vendor.hotel_type)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    {renderVendorStatus(vendor.status)}
                  </Descriptions.Item>

                  <Descriptions.Item label="Contact Person">
                    {vendor.hotel_contact_person}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mobile">
                    {vendor.hotel_mobile_no}
                  </Descriptions.Item>
                  <Descriptions.Item label="WhatsApp">
                    {vendor.hotel_whatsapp_no}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {vendor.hotel_email_id}
                  </Descriptions.Item>

                  <Descriptions.Item label="Website" span={2}>
                    <a href={vendor.url} target="_blank" rel="noreferrer">
                      {vendor.url}
                    </a>
                  </Descriptions.Item>
                </Descriptions>
                <Descriptions
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 1, md: 3, lg: 4 }}
                >
                  <Descriptions.Item label="Area">
                    {vendor.hotel_area}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {/* ================= BILLING (FR-1) ================= */}
                <Descriptions
                  title="Billing"
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 1, md: 3, lg: 4 }}
                >
                  <Descriptions.Item label="Expected Billing Date">
                    {formatDate(vendor.expected_live_date)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Next Billing Date">
                    {formatDate(vendor.next_billing_date)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Billing Cycle">
                    {getBillingCycleLabel(vendor.billing_cycle)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Auto Generation">
                    {vendor.auto_bill_generation ? (
                      <Tag color="green">ON</Tag>
                    ) : (
                      <Tag color="red">OFF</Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {/* ================= SALES MAN ================= */}
                <Descriptions
                  title="Sales Manager (BDE)"
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 1, md: 4 }}
                >
                  <Descriptions.Item label="Name">
                    {vendor.sales_man?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {vendor.sales_man?.phone}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {vendor.sales_man?.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Employee Code">
                    {vendor.sales_man?.employee_code}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {/* ================= PLAN ================= */}
                <Descriptions
                  title="Active Subscription Plan"
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 1, md: 3 }}
                >
                  <Descriptions.Item label="Plan Name">
                    {vendor.active_plan?.plan_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Room Capacity">
                    {vendor.active_plan?.plan_room_capacity_start} -{" "}
                    {vendor.active_plan?.plan_room_capacity_end}
                  </Descriptions.Item>
                  <Descriptions.Item label="Monthly Price">
                    {CURRENCY}
                    {vendor.active_plan?.plan_monthly_price}
                  </Descriptions.Item>
                  <Descriptions.Item label="Yearly Price">
                    {CURRENCY}
                    {vendor.active_plan?.plan_yearly_price}
                  </Descriptions.Item>
                  <Descriptions.Item label="Setup Charge">
                    {CURRENCY}
                    {vendor.setup_charage_amount}
                  </Descriptions.Item>
                  <Descriptions.Item label="Setup Paid">
                    {renderBillStatus(vendor.setup_charage_status)}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {/* ================= ROOMS ================= */}
                <h3 style={{ marginTop: 16 }}>Rooms</h3>
                {vendor.rooms?.map((room) => (
                  <Card
                    key={room.id}
                    type="inner"
                    title={room.room_name}
                    style={{ marginBottom: 16 }}
                  >
                    <Descriptions
                      bordered
                      size="small"
                      column={{ xs: 1, sm: 1, md: 3 }}
                    >
                      <Descriptions.Item label="Quantity">
                        {room.room_qty}
                      </Descriptions.Item>
                      <Descriptions.Item label="Max Guests">
                        {room.max_guests}
                      </Descriptions.Item>
                      <Descriptions.Item label="Room Rate">
                        {CURRENCY}
                        {room.room_rate}
                      </Descriptions.Item>
                      <Descriptions.Item label="Extra Adult Rate">
                        {CURRENCY}
                        {room.extra_adult_rate}
                      </Descriptions.Item>
                      <Descriptions.Item label="Room Numbers" span={2}>
                        {room.room_numbers}
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <strong>Rate Types</strong>
                    <Table
                      size="small"
                      pagination={false}
                      scroll={{ x: 600 }}
                      rowKey="id"
                      dataSource={room.rate_types}
                      columns={[
                        { title: "Room Code", dataIndex: "room_code" },
                        { title: "Service", dataIndex: "room_service" },
                        { title: "Type", dataIndex: "room_type" },
                        {
                          title: "Rate Plan Code",
                          dataIndex: "rate_plan_code",
                        },
                      ]}
                    />
                  </Card>
                ))}

                <Divider />

                {/* ================= DOCUMENTS ================= */}
                <h3 style={{ marginTop: 16 }}>Documents</h3>

                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    {vendor?.documents &&
                      (() => {
                        const status =
                          vendor.documents
                            .doc_property_img_google_drive_link_is_approved;
                        const remark =
                          vendor.documents
                            .doc_property_img_google_drive_link_remark;

                        const getStatusColor = (status) => {
                          if (status === "approved") return "green";
                          if (status === "reject") return "red";
                          return "orange";
                        };

                        return (
                          <Descriptions bordered layout="vertical">
                            <Descriptions.Item label="PAN Card No">
                              {vendor.documents.doc_pan_card_no}
                            </Descriptions.Item>

                            <Descriptions.Item label="Aadhar No">
                              {vendor.documents.doc_aadhar_no}
                            </Descriptions.Item>

                            <Descriptions.Item label="Property IMG Drive Link">
                              <Badge.Ribbon
                                text={status?.toUpperCase()}
                                color={getStatusColor(status)}
                              >
                                <div>
                                  {
                                    vendor.documents
                                      .doc_property_img_google_drive_link
                                  }

                                  {status === "reject" && remark && (
                                    <div
                                      style={{
                                        marginTop: 10,
                                        padding: 8,
                                        background: "#fff1f0",
                                        border: "1px solid #ffa39e",
                                        borderRadius: 6,
                                        color: "#cf1322",
                                        fontSize: 13,
                                      }}
                                    >
                                      <strong>Rejection Reason:</strong>
                                      <div>{remark}</div>
                                    </div>
                                  )}
                                </div>
                              </Badge.Ribbon>
                            </Descriptions.Item>
                          </Descriptions>
                        );
                      })()}
                  </Col>
                  {[
                    {
                      key: "pan_image",
                      title: "PAN Card",
                      status: vendor.documents?.doc_pan_card_is_approved,
                      remark: vendor.documents?.doc_pan_card_remark,
                    },
                    {
                      key: "aadhar_front_image",
                      title: "Aadhar Front",
                      status: vendor.documents?.doc_aadhar_is_approved,
                      remark: vendor.documents?.doc_aadhar_remark,
                    },
                    {
                      key: "aadhar_back_image",
                      title: "Aadhar Back",
                      status: vendor.documents?.doc_aadhar_is_approved,
                      remark: vendor.documents?.doc_aadhar_remark,
                    },
                    {
                      key: "gst_image",
                      title: "GST Certificate",
                      status: vendor.documents?.doc_gst_certificate_is_approved,
                      remark: vendor.documents?.doc_gst_certificate_remark,
                    },
                    {
                      key: "msme_image",
                      title: "MSME Certificate",
                      status:
                        vendor.documents?.doc_msme_certificate_is_approved,
                      remark: vendor.documents?.doc_msme_certificate_remark,
                    },
                    {
                      key: "trade_image",
                      title: "Trade License",
                      status: vendor.documents?.doc_trade_license_is_approved,
                      remark: vendor.documents?.doc_trade_license_remark,
                    },
                    {
                      key: "passbook_image",
                      title: "Bank Passbook",
                      status: vendor.documents?.doc_bank_passbook_is_approved,
                      remark: vendor.documents?.doc_bank_passbook_remark,
                    },
                    {
                      key: "hotel_profile_image",
                      title: "Hotel Profile",
                      status: vendor.documents?.doc_hotel_profile_is_approved,
                      remark: vendor.documents?.doc_hotel_profile_remark,
                    },
                  ].map(
                    ({ key, title, status, remark }) =>
                      vendor.documents?.[key] && (
                        <Col xs={24} sm={12} md={8} lg={6} key={key}>
                          <Badge.Ribbon {...renderStatusText(status)}>
                            <Card title={title}>
                              <Image
                                src={makeFileURL(
                                  vendor.documents[key].filePath,
                                )}
                                width="100%"
                                style={{
                                  maxHeight: 200,
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                                preview
                              />

                              {status === "reject" && remark && (
                                <div
                                  style={{
                                    marginTop: 10,
                                    padding: 8,
                                    background: "#fff1f0",
                                    border: "1px solid #ffa39e",
                                    borderRadius: 6,
                                    color: "#cf1322",
                                    fontSize: 13,
                                  }}
                                >
                                  <strong>Rejection Reason:</strong>
                                  <div>{remark}</div>
                                </div>
                              )}
                            </Card>
                          </Badge.Ribbon>
                        </Col>
                      ),
                  )}
                </Row>
              </>
            )}
          </Spin>
        </Card>
      </Content>

      <LayoutFooter />
    </Layout>
  );
}

export default ViewVendor;
